import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Tree } from '../column-picker/models/column-picker';
import { TreeBuilder } from '../column-picker/tree-builder';
import { filter, startWith, Subscription, tap } from 'rxjs';
import { MetadataService } from '../../../../../../services/metadata.service';
import { MatDialog } from '@angular/material/dialog';
import { openConfirmationDialog } from '../../../../../confirmation-popup/confirmation-popup';
import { SortingItemComponent } from './components/sorting-item/sorting-item.component';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { TabSort } from '../../../../models/utils-tabs';
import { TableType } from '../../../../../../models/shipment';
import { MetadataColumn } from '../../../../../../models/metadata';

interface SortingForm {
  columnKey: string;
  order: number;
  direction: string;
}

@Component({
  selector: 'app-sorting',
  standalone: true,
  templateUrl: './sorting.component.html',
  imports: [MatButton, SortingItemComponent, CdkDrag, CdkDropList, CdkDragPlaceholder],
  styleUrl: './sorting.component.scss'
})
export class SortingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sortingArray!: FormArray<FormGroup>;
  @Input() sorts?: TabSort[];
  @Input() type!: TableType;

  treeNodes: Tree[] = [];

  private treeBuilder = new TreeBuilder('sortable');
  private subscription = new Subscription();

  constructor(
    private metadataService: MetadataService,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.sortingArray.valueChanges
        .pipe(
          startWith(this.sortingArray.getRawValue()),
          tap(formData => {
            this.treeNodes = this.updateColumnAvailability(formData as SortingForm[]);
          })
        )
        .subscribe()
    );
    if (this.sorts) {
      this.sorts.forEach(sort => this.addRule(sort));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sortingArray']) {
      this.subscription.add(
        this.metadataService
          .getFilteredOutMetadata()
          .pipe(
            tap(({ columnGroups, shipmentColumns, orderColumns, containerColumns, orderExceptionColumns }) => {
              const columnsConfig: { [key: string]: MetadataColumn[] } = {
                Shipments: shipmentColumns,
                Containers: containerColumns,
                Orders: orderColumns,
                OrderExceptions: orderExceptionColumns
              };
              const columns = columnsConfig[this.type];
              this.treeNodes = this.treeBuilder.buildTree(columnGroups, columns);
            })
          )
          .subscribe()
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  addRule(sort?: TabSort) {
    const order = this.sortingArray.controls.length;
    const newGroup = new FormGroup({
      order: new FormControl(order),
      columnKey: new FormControl('', Validators.required),
      direction: new FormControl('Asc', Validators.required)
    });
    this.sortingArray.push(newGroup);
    if (sort) {
      newGroup.patchValue(sort);
    }
  }

  deleteRule(index: number) {
    this.subscription.add(
      openConfirmationDialog(this.matDialog, {
        title: 'Delete sorting rule',
        message: 'Are you sure you want to delete this sorting rule?'
      })
        .afterClosed()
        .pipe(
          filter(result => result === 'Confirmed'),
          tap(() => {
            this.sortingArray.removeAt(index);
            this.sortingArray.updateValueAndValidity();
            this.reorderFilters();
          })
        )
        .subscribe()
    );
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<any>) {
    moveItemInArray(this.sortingArray.controls, previousIndex, currentIndex);
    this.sortingArray.controls.forEach((control, index) => control.patchValue({ order: index }));
  }

  private updateColumnAvailability(formData: SortingForm[], nodes?: Tree[]): Tree[] {
    const selectedColumnKeys = formData.map(({ columnKey }) => columnKey);
    const source = nodes ? nodes : this.treeNodes;
    return source.map(node => {
      if (node.children && node.children.length > 0) {
        return { ...node, children: this.updateColumnAvailability(formData, node.children) };
      } else {
        return {
          ...node,
          disabled: selectedColumnKeys.includes(node.key)
        };
      }
    });
  }

  private reorderFilters() {
    this.sortingArray.controls.forEach((control, index) => {
      control.get('order')?.setValue(index);
    });
  }
}
